// src/pages/TemplateMapPage.jsx
import React, { useState, useEffect } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';

const TemplateMapPage = () => {
  // only operational mapping
  const templateKey = 'operational';
  const templateLabel = 'Operational';

  // dropdown data
  const [vessels, setVessels] = useState([]);
  const [operationalTemplates, setOperationalTemplates] = useState([]);

  // form state
  const [currentMapId, setCurrentMapId] = useState(null);
  const [selectedVessel, setSelectedVessel] = useState('');
  const [selectedOperationalTemplate, setSelectedOperationalTemplate] = useState('');
  const [message, setMessage] = useState('');

  // table data
  const [maps, setMaps] = useState([]);

  // Fetch ships once
  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_BASE_URL}/api/ships`)
      .then(r => r.json())
      .then(setVessels)
      .catch(console.error);
  }, []);

  // Fetch operational templates once
  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_BASE_URL}/api/templates/operational`)
      .then(r => r.json())
      .then(setOperationalTemplates)
      .catch(console.error);
  }, []);

  // Load all maps; ensure we always set an array
  const loadMaps = () => {
    fetch(`${process.env.REACT_APP_API_BASE_URL}/api/template-maps`)
      .then(r => {
        if (!r.ok) throw new Error(r.statusText);
        return r.json();
      })
      .then(data => {
        console.log('Fetched template‐maps:', data);
        setMaps(Array.isArray(data) ? data : []);
      })
      .catch(err => {
        console.error('Error fetching template maps:', err);
        setMaps([]);
      });
  };
  useEffect(loadMaps, []);

  // Click row: load mapping into form
  const loadMappingIntoForm = map => {
    setCurrentMapId(map._id);
    setSelectedVessel(map.vessel._id);
    setSelectedOperationalTemplate(map.mappings.operational?._id || '');
    setMessage(`Loaded mapping for ${map.vessel.vesselName}. You can now update and save.`);
  };

  // Save or update mapping
  const handleSave = async () => {
    if (!selectedVessel) {
      setMessage('Please select a vessel.');
      return;
    }
    if (!selectedOperationalTemplate) {
      setMessage(`Please select a ${templateLabel} template.`);
      return;
    }

    const payload = {
      vesselId: selectedVessel,
      mappings: {
        [templateKey]: selectedOperationalTemplate
      }
    };

    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/template-maps`, {
        method: 'POST', // backend upserts by vessel
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error('Save failed');
      setMessage(currentMapId ? 'Mapping updated!' : 'Mapping saved!');
      setCurrentMapId(null);
      setSelectedVessel('');
      setSelectedOperationalTemplate('');
      loadMaps();
    } catch (err) {
      console.error(err);
      setMessage('Error saving mapping');
    }
  };

  // Delete a mapping
  const handleDelete = async id => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/template-maps/${id}`, {
        method: 'DELETE',
      });
      if (!res.ok) throw new Error('Delete failed');
      setMessage('Mapping deleted');
      if (currentMapId === id) {
        setCurrentMapId(null);
        setSelectedVessel('');
        setSelectedOperationalTemplate('');
      }
      loadMaps();
    } catch (err) {
      console.error(err);
      setMessage('Error deleting mapping');
    }
  };

  return (
    <div className="container my-4">
      <h2>Template Map</h2>
      <p>Choose a vessel and assign its Operational template.</p>

      {/* FORM */}
      <div className="mb-4 p-3 border rounded">
        <div className="mb-3">
          <label className="form-label">Vessel</label>
          <select
            className="form-select"
            value={selectedVessel}
            onChange={e => setSelectedVessel(e.target.value)}
          >
            <option value="">-- Select Vessel --</option>
            {vessels.map(s => (
              <option key={s._id} value={s._id}>
                {s.vesselName}
              </option>
            ))}
          </select>
        </div>

        <div className="mb-3">
          <label className="form-label">{templateLabel} Template</label>
          <select
            className="form-select"
            value={selectedOperationalTemplate}
            onChange={e => setSelectedOperationalTemplate(e.target.value)}
          >
            <option value="">-- Select {templateLabel} --</option>
            {operationalTemplates.map(t => (
              <option key={t._id} value={t._id}>
                {t.templateName}
              </option>
            ))}
          </select>
        </div>

        <button className="btn btn-primary" onClick={handleSave}>
          {currentMapId ? 'Update Mapping' : 'Save Mapping'}
        </button>

        {message && <div className="mt-3 alert alert-info">{message}</div>}
      </div>

      {/* TABLE */}
      <h4>Existing Template Maps</h4>
      <table className="table table-bordered">
        <thead>
          <tr>
            <th>Vessel</th>
            <th>{templateLabel}</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {maps.length === 0 ? (
            <tr>
              <td colSpan="3" className="text-center text-muted">
                No mappings found.
              </td>
            </tr>
          ) : (
            maps.map(map => (
              <tr
                key={map._id}
                className={map._id === currentMapId ? 'table-primary' : ''}
                style={{ cursor: 'pointer' }}
                onClick={() => loadMappingIntoForm(map)}
              >
                <td>{map.vessel.vesselName}</td>
                <td>{map.mappings.operational?.templateName || '—'}</td>
                <td>
                  <button
                    className="btn btn-sm btn-danger"
                    onClick={e => {
                      e.stopPropagation();
                      handleDelete(map._id);
                    }}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

export default TemplateMapPage;
